import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Checkbox, Input, Skeleton, TextArea, useToast } from '@bbpos/ui';
import { PLAN_FEATURE_KEYS, type PlanFeatureKey } from '@bbpos/types';
import { tenantService } from '@/services/tenant.service';
import type { LimitSource, TenantLimits } from '@/types/tenant';
import { GLASS_PANEL, cn, toneClasses } from '@/components/features/subscriptions/design-tokens';
import {
  METERED_LIMITS,
  formatLimit,
  groupPlanFeatures,
  limitInputLabel,
  planFeatureLabel,
  type MeteredLimitColumn,
} from '@/components/features/subscriptions/plan-entitlements';

function UsersIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

/** Where the effective value came from: a per-business override, the plan, or neither. */
function SourceBadge({ source }: { source: LimitSource }) {
  if (source === 'OVERRIDE') return <Badge variant="warning">Custom</Badge>;
  if (source === 'PLAN') return <Badge variant="default">From plan</Badge>;
  return <Badge variant="default">No limit</Badge>;
}

/** Bar tone: green under 80% of the ceiling, amber approaching it, red at or over. */
function usageTone(used: number, limit: number | null): 'success' | 'warning' | 'danger' {
  if (limit === null) return 'success';
  if (used >= limit) return 'danger';
  if (used / limit >= 0.8) return 'warning';
  return 'success';
}

const BAR_FILL: Record<'success' | 'warning' | 'danger', string> = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-rose-500',
};

function UsageMeter({
  label,
  icon,
  used,
  limit,
  source,
  planValue,
}: {
  label: string;
  icon: React.ReactNode;
  used: number;
  limit: number | null;
  source: LimitSource;
  planValue: number | null;
}) {
  const tone = usageTone(used, limit);
  const tones = toneClasses(tone);
  // A grandfathered tenant can sit above its ceiling; clamp the bar, not the text.
  const pct = limit === null ? 0 : Math.min((used / limit) * 100, 100);

  return (
    <div className="rounded-xl border border-neutral-100 dark:border-neutral-700 p-4 transition-all duration-200 hover:shadow-md">
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg', tones.icon)}>{icon}</div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
        </div>
        <SourceBadge source={source} />
      </div>

      <p className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">
        {used}
        <span className="text-base font-medium text-neutral-400 dark:text-neutral-500">
          {limit === null ? ' / ∞' : ` / ${limit}`}
        </span>
      </p>

      {limit !== null && (
        <div className="mt-3 h-2 w-full rounded-full bg-neutral-100 dark:bg-neutral-800">
          <div
            className={cn('h-2 rounded-full transition-all duration-500', BAR_FILL[tone])}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      <p className="mt-2 text-xs text-neutral-400 dark:text-neutral-500">
        {limit === null
          ? 'No ceiling — the tenant is on no plan.'
          : used > limit
            ? `Over limit by ${used - limit}. Existing records are kept; new ones are blocked.`
            : used === limit
              ? 'At the limit — new ones are blocked.'
              : `${limit - used} remaining`}
        {source === 'OVERRIDE' && planValue !== null && ` · plan allows ${planValue}`}
      </p>
    </div>
  );
}

function BoltIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

/**
 * The API budget has no stored count to meter against — it is a rolling 60s
 * window in Redis — so this shows the ceiling and its source without a bar.
 */
function RateLimitRow({
  limit,
  source,
  planValue,
}: {
  limit: number | null;
  source: LimitSource;
  planValue: number | null;
}) {
  const tones = toneClasses(limit === null ? 'neutral' : 'info');
  return (
    <div className="rounded-xl border border-neutral-100 dark:border-neutral-700 p-4 transition-all duration-200 hover:shadow-md sm:col-span-2">
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg', tones.icon)}>
          <BoltIcon />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            API requests per minute
          </p>
        </div>
        <SourceBadge source={source} />
      </div>

      <p className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">
        {limit === null ? '∞' : limit}
        <span className="text-base font-medium text-neutral-400 dark:text-neutral-500"> / min</span>
      </p>

      <p className="mt-2 text-xs text-neutral-400 dark:text-neutral-500">
        One budget across every endpoint. Billing, orders and offline sync are exempt, so a breach
        never stops a sale.
        {source === 'OVERRIDE' && planValue !== null && ` · plan allows ${planValue}`}
      </p>
    </div>
  );
}

/** A monthly / storage / product limit: the effective value and where it came from. */
function LimitRow({ column, data }: { column: MeteredLimitColumn; data: TenantLimits }) {
  const limit = METERED_LIMITS.find((l) => l.column === column)!;
  const source = data.source[column] ?? 'NONE';
  return (
    <div className="rounded-xl border border-neutral-100 dark:border-neutral-700 p-3 transition-all duration-200 hover:shadow-md">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{limit.label}</p>
        <SourceBadge source={source} />
      </div>
      <p className="mt-1 text-lg font-bold tabular-nums text-neutral-900 dark:text-white">
        {formatLimit(limit, data[column] ?? null)}
      </p>
      {source === 'OVERRIDE' && (
        <p className="text-xs text-neutral-400 dark:text-neutral-500">
          Plan allows {formatLimit(limit, data.plan[column] ?? null)}
        </p>
      )}
    </div>
  );
}

function emptyMeteredInputs(): Record<MeteredLimitColumn, string> {
  const inputs = {} as Record<MeteredLimitColumn, string>;
  for (const l of METERED_LIMITS) inputs[l.column] = '';
  return inputs;
}

/**
 * Effective user/location ceilings, where each came from, and how much of it the
 * tenant has consumed. ADMIN+ can override a plan value for this one tenant, or
 * clear the override back to the plan.
 */
export function TenantLimitsPanel({ tenantId, canManage }: { tenantId: string; canManage: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [usersInput, setUsersInput] = useState('');
  const [locationsInput, setLocationsInput] = useState('');
  const [rateInput, setRateInput] = useState('');
  const [meteredInputs, setMeteredInputs] = useState(emptyMeteredInputs);
  const [extraFeatures, setExtraFeatures] = useState<PlanFeatureKey[]>([]);
  const [notes, setNotes] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tenant-limits', tenantId],
    queryFn: () => tenantService.getLimits(tenantId),
  });

  // Seed the form from the current overrides whenever the editor opens or the
  // saved values change under it.
  useEffect(() => {
    if (!data) return;
    setUsersInput(data.overrides.max_users?.toString() ?? '');
    setLocationsInput(data.overrides.max_locations?.toString() ?? '');
    setRateInput(data.overrides.max_requests_per_minute?.toString() ?? '');
    const metered = emptyMeteredInputs();
    for (const l of METERED_LIMITS) metered[l.column] = data.overrides[l.column]?.toString() ?? '';
    setMeteredInputs(metered);
    setExtraFeatures(data.overrides.extra_features ?? []);
    setNotes(data.overrides.notes ?? '');
  }, [data]);

  const planFeatures: readonly string[] = data?.plan.features ?? [];

  const saveMutation = useMutation({
    mutationFn: () => {
      const parse = (raw: string) => {
        const trimmed = raw.trim();
        // Blank clears the override, so the plan value applies again.
        if (trimmed === '') return null;
        const n = Number.parseInt(trimmed, 10);
        return Number.isFinite(n) ? n : null;
      };
      const metered = {} as Record<MeteredLimitColumn, number | null>;
      for (const l of METERED_LIMITS) metered[l.column] = parse(meteredInputs[l.column]);
      return tenantService.updateLimits(tenantId, {
        max_users: parse(usersInput),
        max_locations: parse(locationsInput),
        max_requests_per_minute: parse(rateInput),
        ...metered,
        // The complete list, in registry order. A feature the plan already has
        // adds nothing, so it is not stored as extra.
        extra_features: PLAN_FEATURE_KEYS.filter(
          (k) => extraFeatures.includes(k) && !planFeatures.includes(k),
        ),
        notes: notes.trim(),
      });
    },
    onSuccess: () => {
      toast({ title: 'Limits updated', variant: 'success' });
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['tenant-limits', tenantId] });
      // The usage card measures against these limits.
      queryClient.invalidateQueries({ queryKey: ['tenant-usage', tenantId] });
      // The detail header renders usage against these ceilings.
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
    onError: (err: Error) => {
      toast({ title: err.message || 'Failed to update limits', variant: 'error' });
    },
  });

  if (isLoading) {
    return (
      <div className={cn(GLASS_PANEL, 'p-6')}>
        <Skeleton className="h-5 w-40" />
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={cn(GLASS_PANEL, 'p-6')}>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Could not load limits.</p>
      </div>
    );
  }

  const extras = data.overrides.extra_features ?? [];

  return (
    <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in transition-shadow duration-200 hover:shadow-lg')}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500">
          Limits &amp; Usage
        </h3>
        {canManage && !editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edit limits
          </Button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <UsageMeter
          label="Users"
          icon={<UsersIcon />}
          used={data.usage.users}
          limit={data.max_users}
          source={data.source.max_users}
          planValue={data.plan.max_users}
        />
        <UsageMeter
          label="Locations"
          icon={<PinIcon />}
          used={data.usage.locations}
          limit={data.max_locations}
          source={data.source.max_locations}
          planValue={data.plan.max_locations}
        />
        <RateLimitRow
          limit={data.max_requests_per_minute}
          source={data.source.max_requests_per_minute}
          planValue={data.plan.max_requests_per_minute}
        />
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {METERED_LIMITS.map((l) => (
          <LimitRow key={l.column} column={l.column} data={data} />
        ))}
      </div>
      <p className="mt-2 text-xs text-neutral-400 dark:text-neutral-500">
        This month&apos;s use of these limits is shown at the top of the page.
      </p>

      {!editing && (
        <div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/50 p-4 dark:border-violet-900/50 dark:bg-violet-900/10">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-violet-900 dark:text-violet-100">Extra features</p>
            {extras.length > 0 && <Badge variant="warning">Custom</Badge>}
          </div>
          {extras.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {extras.map((k) => (
                <span
                  key={k}
                  className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-700 dark:bg-violet-900/40 dark:text-violet-200"
                >
                  {planFeatureLabel(k)}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              None. This business has only the features in its plan.
            </p>
          )}
        </div>
      )}

      {!editing && data.overrides.notes && (
        <p className="mt-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
          {data.overrides.notes}
        </p>
      )}

      {editing && (
        <div className="mt-5 rounded-xl bg-neutral-50/70 p-4 dark:bg-neutral-800/40 animate-slide-up">
          <p className="mb-4 text-xs text-neutral-500 dark:text-neutral-400">
            Leave a field blank to use the{' '}
            <span className="font-medium">{data.plan_name ?? 'plan'}</span> value
            {data.plan.max_users !== null && data.plan.max_locations !== null && (
              <> ({data.plan.max_users} users, {data.plan.max_locations} locations)</>
            )}
            . A limit below current usage is rejected.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="limit-users"
                className="mb-2 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
              >
                Max users
              </label>
              <Input
                id="limit-users"
                type="number"
                min={1}
                value={usersInput}
                onChange={setUsersInput}
                placeholder={data.plan.max_users?.toString() ?? 'No limit'}
              />
            </div>
            <div>
              <label
                htmlFor="limit-locations"
                className="mb-2 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
              >
                Max locations
              </label>
              <Input
                id="limit-locations"
                type="number"
                min={1}
                value={locationsInput}
                onChange={setLocationsInput}
                placeholder={data.plan.max_locations?.toString() ?? 'No limit'}
              />
            </div>
            <div className="sm:col-span-2">
              <label
                htmlFor="limit-rate"
                className="mb-2 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
              >
                API requests per minute
              </label>
              <Input
                id="limit-rate"
                type="number"
                min={1}
                value={rateInput}
                onChange={setRateInput}
                placeholder={data.plan.max_requests_per_minute?.toString() ?? 'No limit'}
              />
            </div>
            {METERED_LIMITS.map((l) => (
              <div key={l.column}>
                <label
                  htmlFor={`limit-${l.column}`}
                  className="mb-2 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
                >
                  {limitInputLabel(l)}
                </label>
                <Input
                  id={`limit-${l.column}`}
                  type="number"
                  min={0}
                  value={meteredInputs[l.column]}
                  onChange={(v) => setMeteredInputs((m) => ({ ...m, [l.column]: v }))}
                  placeholder={data.plan[l.column]?.toString() ?? 'No limit'}
                />
              </div>
            ))}
          </div>

          <div className="mt-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Extra features
            </p>
            <p className="mb-3 text-xs text-neutral-500 dark:text-neutral-400">
              Give this business features that are not in its plan.
            </p>
            {PLAN_FEATURE_KEYS.every((k) => planFeatures.includes(k)) ? (
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Its plan already has every feature.
              </p>
            ) : (
              <div className="space-y-3">
                {groupPlanFeatures((k) => !planFeatures.includes(k)).map((group) => (
                  <div key={group.title}>
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
                      {group.title}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {group.features.map((f) => (
                        <div
                          key={f.key}
                          className="rounded-lg bg-white/70 px-3 py-2 transition-all duration-200 hover:bg-white dark:bg-neutral-900/50 dark:hover:bg-neutral-900"
                        >
                          <Checkbox
                            id={`extra-feature-${f.key}`}
                            label={f.label}
                            checked={extraFeatures.includes(f.key)}
                            onChange={(on) =>
                              setExtraFeatures((cur) =>
                                on ? [...cur, f.key] : cur.filter((k) => k !== f.key),
                              )
                            }
                          />
                          <p className="ml-7 text-xs text-neutral-500 dark:text-neutral-400">
                            {f.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Note (why this tenant differs)
            </label>
            <TextArea
              value={notes}
              onChange={setNotes}
              placeholder="e.g. Third branch agreed with the account manager"
              rows={2}
            />
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setEditing(false)}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
            >
              Save limits
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
